import {Button, DatePicker, Input} from 'antd';
import {useCallback, useState} from 'react';

import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {LangKeyType} from '../../../provider/locale/translation/type';
import {
    PostSelectorCompanyType,
    UseCompaniesSelectorHookType,
} from '../../../service/feature-companies/feature-companies-type';
import {useFormRules} from '../../../service/form/form-rules-hook';
import {deletePromotionImage} from '../../../service/promotions/promotions-api';
import {useUploadPromotionImage} from '../../../service/promotions/promotions-hook';
import {
    CommonPromotionStatusEnum,
    PromotionFormFieldsEnum,
    PromotionFormType,
} from '../../../service/promotions/promotions-type';
import {Form} from '../../../typings/antd';
import {noop} from '../../../util/function';
import {PublicationBrandOrCompaniesField} from '../../publication-wizard/publication-brand-or-companies-field/publication-brand-or-companies-field';
import {
    PublicationCompaniesSourceEnum,
    selectBrandCompanies,
} from '../../publication-wizard/publication-brand-or-companies-field/publication-brand-or-companies-helper';
import {PublicationFieldLabel} from '../../publication-wizard/publication-field-label';
import {PublicationFooterButtonTooltip} from '../../publication-wizard/publication-footer-button-tooltip';
import * as styles from '../../publication-wizard/publication-form-layout.scss';
import {PublicationMediaUploader} from '../../publication-wizard/publication-media-uploader';
import {PublicationTitleInput} from '../../publication-wizard/publication-title-input';
import {Text} from '../../text/text';

import {isPromotionFormFilled} from './promotion-form-helper';

// Yandex help limits for promotions.
const PROMOTION_TITLE_MAX_LENGTH = 70;
const PROMOTION_DESCRIPTION_MAX_LENGTH = 200;

// Yandex help for promotions: JPG/PNG, single image, minimum 900 × 480 px.
const PROMOTION_MEDIA_MIN_WIDTH = 900;
const PROMOTION_MEDIA_MIN_HEIGHT = 480;
const PROMOTION_MEDIA_MAX_WIDTH = 5000;
const PROMOTION_MEDIA_MAX_HEIGHT = 5000;

const mediaTooltip = (
    <>
        <Text block inheritColor stringKey="PROMOTIONS__FORM__UPLOAD_IMAGES__SINGLE" />
        <Text block inheritColor stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_2" />
        <Text
            block
            inheritColor
            stringKey="PROMOTIONS__FORM__UPLOAD_IMAGES__RESOLUTION"
            valueMap={{minHeight: PROMOTION_MEDIA_MIN_HEIGHT, minWidth: PROMOTION_MEDIA_MIN_WIDTH}}
        />
    </>
);

type SubmittingActionType = 'draft' | 'submit' | null;

type PropsType = {
    onSubmit: (values: PromotionFormType, status: CommonPromotionStatusEnum) => Promise<void>;
    // Closes the modal straight away, used once the promotion has been saved.
    closeModal: () => void;
    // Asks to confirm discarding the changes first, shared with the modal's own close control.
    onCancel: () => void;
    mode: 'edit' | 'create';
    companiesSelector: UseCompaniesSelectorHookType<PostSelectorCompanyType>;
    initialValues?: Partial<PromotionFormType>;
};

// Single-step promotion form: companies/brand, the promotion details and its photo in one modal.
export function PromotionForm(props: PropsType): JSX.Element {
    const {onSubmit, closeModal, onCancel, mode, companiesSelector, initialValues} = props;

    const [form] = Form.useForm<PromotionFormType>();
    const {getLocalizedString} = useLocale();
    const {requiredFieldRule, requiredDateRangeFieldRule, maxLengthFieldRule, validUrlRule} = useFormRules();
    const {mutateAsync: uploadImage} = useUploadPromotionImage();

    // An existing promotion is only known by its companies, so editing always starts on that tab.
    const [companiesSource, setCompaniesSource] = useState<PublicationCompaniesSourceEnum>(
        mode === 'edit' ? PublicationCompaniesSourceEnum.Companies : PublicationCompaniesSourceEnum.Brand
    );
    const [submittingAction, setSubmittingAction] = useState<SubmittingActionType>(null);
    const [isMediaBlocked, setIsMediaBlocked] = useState<boolean>(false);

    const values = Form.useWatch([], {form, preserve: true});

    const handleSubmit = useCallback(
        async (status: CommonPromotionStatusEnum) => {
            setSubmittingAction(status === CommonPromotionStatusEnum.Draft ? 'draft' : 'submit');

            const isSubmitted = await form
                .validateFields()
                .then(async () => {
                    const formValues = form.getFieldsValue(true);

                    if (companiesSource === PublicationCompaniesSourceEnum.Brand && formValues.brandId) {
                        await selectBrandCompanies(companiesSelector, formValues.brandId);
                    }

                    await onSubmit(formValues, status);

                    return true;
                })
                // Field errors are rendered inline and request errors are reported by the mutation's
                // snackbar, so a rejection here only means the modal has to stay open.
                .catch(() => false);

            setSubmittingAction(null);

            if (isSubmitted) {
                closeModal();
            }
        },
        [closeModal, companiesSelector, companiesSource, form, onSubmit]
    );

    const isSubmitDisabled = isMediaBlocked || !isPromotionFormFilled(values, companiesSource);
    const isBusy = submittingAction !== null;

    // While an upload is in flight (or has failed) there is nothing left for the user to fill in, so
    // the generic "fill in the required fields" hint would send them looking for a field that is fine.
    const disabledTooltipLangKey: LangKeyType = isMediaBlocked
        ? 'POSTS_FORM__MEDIA_IN_PROGRESS_ERROR'
        : 'PUBLICATION_FORM__FILL_REQUIRED_FIELDS';

    return (
        <Form<PromotionFormType> form={form} initialValues={initialValues} layout="vertical">
            <PublicationBrandOrCompaniesField
                companiesSelector={companiesSelector}
                disabled={isBusy}
                onSourceChange={setCompaniesSource}
                source={companiesSource}
            />

            <Form.Item<PromotionFormType>
                label={<Locale stringKey="PROMOTIONS__FORM__TITLE" />}
                name={PromotionFormFieldsEnum.Title}
                rules={[requiredFieldRule, maxLengthFieldRule(PROMOTION_TITLE_MAX_LENGTH)]}
            >
                <PublicationTitleInput
                    maxLength={PROMOTION_TITLE_MAX_LENGTH}
                    placeholder={getLocalizedString('PROMOTIONS__FORM__TITLE__PLACEHOLDER')}
                />
            </Form.Item>

            <Form.Item<PromotionFormType>
                label={<Locale stringKey="PROMOTIONS__FORM__DESCRIPTION" />}
                name={PromotionFormFieldsEnum.Description}
                rules={[requiredFieldRule, maxLengthFieldRule(PROMOTION_DESCRIPTION_MAX_LENGTH)]}
            >
                <Input.TextArea
                    autoSize={{minRows: 3, maxRows: 8}}
                    maxLength={PROMOTION_DESCRIPTION_MAX_LENGTH}
                    placeholder={getLocalizedString('PROMOTIONS__FORM__DESCRIPTION__PLACEHOLDER')}
                    showCount
                />
            </Form.Item>

            <Form.Item<PromotionFormType>
                label={<Locale stringKey="PROMOTIONS__FORM__PERIOD" />}
                name={PromotionFormFieldsEnum.Period}
                rules={[requiredDateRangeFieldRule()]}
            >
                <DatePicker.RangePicker
                    className={styles.PublicationFormLayout_fullWidthControl}
                    placeholder={[
                        getLocalizedString('PROMOTIONS__FORM__START_AT'),
                        getLocalizedString('PROMOTIONS__FORM__FINISH_AT'),
                    ]}
                />
            </Form.Item>

            {/* Optional, exactly like in Yandex Business. */}
            <Form.Item<PromotionFormType>
                label={<Locale stringKey="PROMOTIONS__FORM__URL" />}
                name={PromotionFormFieldsEnum.Url}
                rules={[validUrlRule()]}
            >
                <Input placeholder={getLocalizedString('PROMOTIONS__FORM__URL__PLACEHOLDER')} />
            </Form.Item>

            {/* Optional too: Yandex accepts a promotion without a photo. */}
            <Form.Item<PromotionFormType>
                label={
                    <PublicationFieldLabel
                        iconPosition="after"
                        labelLangKey="PROMOTIONS__FORM__IMAGES"
                        tooltip={mediaTooltip}
                    />
                }
                name={PromotionFormFieldsEnum.Images}
            >
                <PublicationMediaUploader
                    layout="list"
                    maxCount={1}
                    maxHeight={PROMOTION_MEDIA_MAX_HEIGHT}
                    maxWidth={PROMOTION_MEDIA_MAX_WIDTH}
                    minHeight={PROMOTION_MEDIA_MIN_HEIGHT}
                    minWidth={PROMOTION_MEDIA_MIN_WIDTH}
                    onBlockedChange={setIsMediaBlocked}
                    remove={(imageId) => deletePromotionImage(imageId).catch(noop)}
                    upload={(file) => uploadImage(file)}
                    uploaderTextLangKey="PROMOTIONS__FORM__UPLOAD_IMAGES__BUTTON"
                />
            </Form.Item>

            <div className={styles.PublicationFormLayout_footer}>
                <Button disabled={isBusy} onClick={onCancel}>
                    <Locale stringKey="BUTTON__CANCEL" />
                </Button>

                {mode === 'create' && (
                    <PublicationFooterButtonTooltip
                        isDisabled={isMediaBlocked}
                        langKey="POSTS_FORM__MEDIA_IN_PROGRESS_ERROR"
                    >
                        <Button
                            // The photo is optional, so nothing else would stop a draft from being saved
                            // mid-upload — with the image left orphaned on the server.
                            disabled={isBusy || isMediaBlocked}
                            loading={submittingAction === 'draft'}
                            onClick={() => handleSubmit(CommonPromotionStatusEnum.Draft)}
                        >
                            <Locale stringKey="POSTS_FORM__BUTTON__SAVE_DRAFT" />
                        </Button>
                    </PublicationFooterButtonTooltip>
                )}

                <PublicationFooterButtonTooltip isDisabled={isSubmitDisabled} langKey={disabledTooltipLangKey}>
                    <Button
                        disabled={isBusy || isSubmitDisabled}
                        loading={submittingAction === 'submit'}
                        onClick={() => handleSubmit(CommonPromotionStatusEnum.Sent)}
                        type="primary"
                    >
                        <Locale stringKey={mode === 'create' ? 'PROMOTIONS__FORM__LAUNCH' : 'PROMOTIONS__FORM__SAVE'} />
                    </Button>
                </PublicationFooterButtonTooltip>
            </div>
        </Form>
    );
}
